import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { Add, ChevronLeft, ChevronRight, Download, FilterList, Search } from '@mui/icons-material';
import { AxiosError } from 'axios';
import apiClient from '../utils/apiClient';
import { DsBadge, DsButton, DsField } from '../design-system/components';
import './ManagementList.css';
import './ContentManagementPage.css';

type Kind = 'notices' | 'banners';
type Entry = {
  id: number;
  status: string | number;
  title?: string;
  image_path?: string;
  link?: string | null;
  created_at?: string;
  open_date?: string;
  close_date?: string | null;
};
type Form = { status: number; title: string; link: string; open_date: string; close_date: string; image: File | null };
const emptyForm = (): Form => ({ status: 1, title: '', link: '', open_date: '', close_date: '', image: null });
const displayLink = (link?: string | null): string =>
  link && link.trim().toLowerCase() !== 'null' ? link : '';
const errorMessage = (error: unknown): string => {
  const response = (error as AxiosError<{ message?: string; errors?: Record<string, string[]> }>).response;
  const first = response?.data?.errors && Object.values(response.data.errors)[0]?.[0];
  return first || response?.data?.message || '処理に失敗しました。時間をおいて再度お試しください。';
};
const isOpen = (status: Entry['status']) => status === 2 || status === '2' || status === 'OPEN';

export const ContentManagementPage: React.FC<{ kind: Kind }> = ({ kind }) => {
  const label = kind === 'notices' ? 'お知らせ' : 'バナー';
  const [rows, setRows] = useState<Entry[]>([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [status, setStatus] = useState('');
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [appliedRange, setAppliedRange] = useState<[string, string]>(['', '']);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selected, setSelected] = useState<number[]>([]);
  const [editing, setEditing] = useState<Entry | null | undefined>(undefined);
  const [form, setForm] = useState<Form>(emptyForm());
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageDragging, setImageDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dialogError, setDialogError] = useState('');

  useEffect(() => {
    if (!form.image) { setImagePreview(null); return; }
    const url = URL.createObjectURL(form.image);
    setImagePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [form.image]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const response = await apiClient.get<{ list: Entry[]; totalHits: number; limit: number }>(`/api/admin/content/${kind}`, {
        params: {
          page,
          status: status ? [Number(status)] : undefined,
          title: kind === 'notices' ? appliedSearch || undefined : undefined,
          created_at: kind === 'notices' && appliedRange[0] && appliedRange[1] ? appliedRange : undefined,
          post_range: kind === 'banners' && appliedRange[0] && appliedRange[1] ? appliedRange : undefined,
        },
      });
      setRows(response.data.list);
      setTotal(response.data.totalHits);
      setPages(Math.max(1, Math.ceil(response.data.totalHits / response.data.limit)));
      setSelected([]);
    } catch (err) { setError(errorMessage(err)); }
    finally { setLoading(false); }
  }, [kind, page, status, appliedSearch, appliedRange]);
  useEffect(() => { void load(); }, [load]);

  const applyFilters = () => {
    if (rangeStart && rangeEnd && rangeStart > rangeEnd) {
      setError('開始日は終了日以前にしてください。'); return;
    }
    setPage(1); setAppliedSearch(search.trim());
    setAppliedRange([rangeStart, rangeEnd]); setFiltersOpen(false);
  };
  const clearFilters = () => {
    setSearch(''); setAppliedSearch(''); setStatus('');
    setRangeStart(''); setRangeEnd(''); setAppliedRange(['', '']); setPage(1);
  };
  const openCreate = () => { setForm(emptyForm()); setDialogError(''); setImageDragging(false); setEditing(null); };
  const openEdit = async (row: Entry) => {
    setError(''); setDialogError('');
    try {
      const response = await apiClient.get<Entry>(`/api/admin/content/${kind}/${row.id}`);
      const item = response.data;
      setForm({ status: Number(item.status), title: item.title || '', link: displayLink(item.link), open_date: item.open_date || '', close_date: item.close_date || '', image: null });
      setImageDragging(false);
      setEditing(row);
    } catch (err) { setError(errorMessage(err)); }
  };
  const save = async () => {
    if (kind === 'notices' && !form.title.trim()) { setDialogError('タイトルを入力してください。'); return; }
    if (kind === 'banners' && (!form.open_date || (!editing && !form.image))) { setDialogError('掲載開始日と画像を指定してください。'); return; }
    if (form.close_date && form.close_date < form.open_date) { setDialogError('掲載終了日は開始日以降にしてください。'); return; }
    setSaving(true); setDialogError('');
    try {
      const path = `/api/admin/content/${kind}${editing?.id ? `/${editing.id}` : ''}`;
      if (kind === 'banners') {
        const data = new FormData();
        data.append('status', String(form.status)); data.append('open_date', form.open_date); data.append('link', form.link);
        if (form.close_date) data.append('close_date', form.close_date);
        if (form.image) data.append('image', form.image);
        await apiClient.post(path, data, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await apiClient.post(path, { status: form.status, title: form.title.trim(), link: form.link || null });
      }
      setEditing(undefined); await load();
    } catch (err) { setDialogError(errorMessage(err)); }
    finally { setSaving(false); }
  };
  const bulk = async (nextStatus: number) => {
    if (!selected.length) return;
    setSaving(true); setError('');
    try {
      await apiClient.post(`/api/admin/content/${kind}/status`, { targetIds: selected, status: nextStatus });
      await load();
    } catch (err) { setError(errorMessage(err)); }
    finally { setSaving(false); }
  };
  const toggleAll = (checked: boolean) => setSelected(checked ? rows.map(row => row.id) : []);
  const chooseImage = (file?: File) => {
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setDialogError('JPEG・PNG・WebP形式の画像を選択してください。'); return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setDialogError('画像は5MB以下にしてください。'); return;
    }
    setDialogError('');
    setForm(current => ({ ...current, image: file }));
  };

  return <div className="management-page content-management-page">
    <div className="management-heading"><div><h1>{label}管理</h1><p>マイページに表示する{label}の登録と公開設定</p></div><div className="management-heading-actions"><DsButton tone="primary" icon={<Add />} onClick={openCreate}>{label}を追加</DsButton></div></div>
    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    <section className="management-panel" aria-label={`${label}の検索と絞り込み`}>
      <div className="management-tabs" role="tablist" aria-label="公開状態">
        {[{ value: '', label: 'すべて' }, { value: '2', label: '公開' }, { value: '1', label: '非公開' }].map(tab =>
          <button key={tab.value} type="button" role="tab" aria-selected={status === tab.value} onClick={() => { setStatus(tab.value); setPage(1); }}>{tab.label}</button>
        )}
      </div>
      <form className="management-controls" role="search" onSubmit={event => { event.preventDefault(); applyFilters(); }}>
        {kind === 'notices' && <label className="management-search"><Search fontSize="small" /><input aria-label="タイトルを検索" placeholder="タイトルで検索" value={search} onChange={event => setSearch(event.target.value)} /></label>}
        <DsButton type="submit" tone="primary">検索</DsButton>
        <DsButton type="button" icon={<FilterList />} aria-expanded={filtersOpen} aria-controls="content-detail-filters" onClick={() => setFiltersOpen(!filtersOpen)}>詳細絞り込み{(appliedRange[0] || appliedRange[1]) && <span className="management-filter-count">●</span>}</DsButton>
      </form>
      {filtersOpen && <div id="content-detail-filters" className="management-filter-fields">
        <label>{kind === 'notices' ? '作成日（開始）' : '掲載期間（開始）'}<input className="ds-input" type="date" value={rangeStart} onChange={event => setRangeStart(event.target.value)} /></label>
        <label>{kind === 'notices' ? '作成日（終了）' : '掲載期間（終了）'}<input className="ds-input" type="date" value={rangeEnd} onChange={event => setRangeEnd(event.target.value)} /></label>
        <p className="management-filter-help">条件を選び、上の「検索」で適用します</p>
      </div>}
      {(appliedSearch || status || appliedRange[0] || appliedRange[1]) && <div className="management-applied"><span>適用中</span>{appliedSearch && <button onClick={() => { setSearch(''); setAppliedSearch(''); setPage(1); }}>タイトル: {appliedSearch} ×</button>}{status && <button onClick={() => { setStatus(''); setPage(1); }}>状態: {status === '2' ? '公開' : '非公開'} ×</button>}{appliedRange[0] && appliedRange[1] && <button onClick={() => { setRangeStart(''); setRangeEnd(''); setAppliedRange(['', '']); setPage(1); }}>{appliedRange.join(' ～ ')} ×</button>}<button onClick={clearFilters}>すべてクリア</button></div>}
    </section>
    <section className="management-panel" aria-label={`${label}一覧`}>
      <div className="management-results-head"><h2>検索結果</h2><span>{loading ? '読み込み中…' : `${total} 件の${label}`}</span></div>
      <div className="content-bulk-actions"><span>{selected.length} 件選択中</span><DsButton type="button" disabled={!selected.length || saving} onClick={() => void bulk(2)}>公開にする</DsButton><DsButton type="button" disabled={!selected.length || saving} onClick={() => void bulk(1)}>非公開にする</DsButton></div>
      <div className="management-table-wrap"><table className="management-table content-table"><thead><tr><th><input type="checkbox" aria-label="すべて選択" checked={rows.length > 0 && selected.length === rows.length} onChange={event => toggleAll(event.target.checked)} /></th><th>ID</th><th>{kind === 'notices' ? 'タイトル' : '画像'}</th><th>状態</th><th>リンク</th><th>{kind === 'notices' ? '作成日時' : '掲載期間'}</th><th>操作</th></tr></thead><tbody>
        {rows.map(row => <tr key={row.id}><td><input type="checkbox" aria-label={`${row.id} を選択`} checked={selected.includes(row.id)} onChange={event => setSelected(current => event.target.checked ? [...current, row.id] : current.filter(id => id !== row.id))} /></td><td>{row.id}</td><td>{kind === 'notices' ? <strong>{row.title}</strong> : row.image_path && <img className="content-banner-thumbnail" src={row.image_path} alt={`バナー ${row.id}`} />}</td><td><DsBadge tone={isOpen(row.status) ? 'success' : 'neutral'}>{isOpen(row.status) ? '公開' : '非公開'}</DsBadge></td><td className="content-link-cell">{displayLink(row.link) || '—'}</td><td>{kind === 'notices' ? row.created_at : `${row.open_date || ''} ～ ${row.close_date || '無期限'}`}</td><td><button className="management-link" onClick={() => void openEdit(row)}>編集</button></td></tr>)}
      </tbody></table>{!loading && !rows.length && <div className="management-empty">該当する{label}はありません。</div>}</div>
      <div className="management-mobile-list">{rows.map(row => <div className="content-mobile-item" key={row.id}><label><input type="checkbox" checked={selected.includes(row.id)} onChange={event => setSelected(current => event.target.checked ? [...current, row.id] : current.filter(id => id !== row.id))} /> ID {row.id}</label><DsBadge tone={isOpen(row.status) ? 'success' : 'neutral'}>{isOpen(row.status) ? '公開' : '非公開'}</DsBadge><strong>{kind === 'notices' ? row.title : row.image_path && <img className="content-banner-thumbnail" src={row.image_path} alt={`バナー ${row.id}`} />}</strong><span>{kind === 'notices' ? row.created_at : `${row.open_date || ''} ～ ${row.close_date || '無期限'}`}</span><button className="management-link" onClick={() => void openEdit(row)}>編集する</button></div>)}</div>
      <div className="management-pagination"><span>{total} 件中 {total ? (page - 1) * 20 + 1 : 0}～{Math.min(page * 20, total)} 件</span><div className="management-pagination-actions"><button aria-label="前のページ" disabled={page <= 1} onClick={() => setPage(page - 1)}><ChevronLeft fontSize="small" /></button><span>{page} / {pages}</span><button aria-label="次のページ" disabled={page >= pages} onClick={() => setPage(page + 1)}><ChevronRight fontSize="small" /></button></div></div>
    </section>
    <Dialog className="management-dialog" open={editing !== undefined} onClose={() => setEditing(undefined)} fullWidth maxWidth="sm"><DialogTitle>{label}{editing ? 'を編集' : 'を追加'}</DialogTitle><DialogContent>
      {dialogError && <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert>}
      <div className={`management-form-grid${kind === 'notices' ? ' content-notice-form' : ''}`}>
        <DsField label="ステータス"><select className="ds-select" value={form.status} onChange={event => setForm({ ...form, status: Number(event.target.value) })}><option value={1}>非公開</option><option value={2}>公開</option></select></DsField>
        {kind === 'notices' && <DsField label="タイトル *" hint={`改行もマイページに表示されます（${form.title.length}/255文字）`}><textarea className="ds-textarea content-notice-textarea" required maxLength={255} rows={7} value={form.title} onChange={event => setForm({ ...form, title: event.target.value })} /></DsField>}
        <div className="management-wide"><DsField label="リンクURL"><input className="ds-input" type="url" placeholder="https://" value={form.link} onChange={event => setForm({ ...form, link: event.target.value })} /></DsField></div>
        {kind === 'banners' && <>
          <DsField label="掲載開始日 *"><input className="ds-input" type="date" required value={form.open_date} onChange={event => setForm({ ...form, open_date: event.target.value })} /></DsField>
          <DsField label="掲載終了日"><input className="ds-input" type="date" value={form.close_date} onChange={event => setForm({ ...form, close_date: event.target.value })} /></DsField>
          <div className="management-wide ds-field">
            <span className="ds-field-label">{editing ? '画像を変更する場合' : '画像 *'}</span>
            <label className={`content-media-picker${imageDragging ? ' content-media-picker--dragging' : ''}`}
              onDragOver={event => { event.preventDefault(); setImageDragging(true); }}
              onDragLeave={() => setImageDragging(false)}
              onDrop={event => { event.preventDefault(); setImageDragging(false); chooseImage(event.dataTransfer.files[0]); }}>
              <input type="file" accept="image/jpeg,image/png,image/webp" aria-label="バナー画像を選択"
                onChange={event => { chooseImage(event.target.files?.[0]); event.target.value = ''; }} />
              {(imagePreview || editing?.image_path) ? <img src={imagePreview || editing?.image_path} alt="バナー画像のプレビュー" /> : <span className="content-media-plus" aria-hidden="true">＋</span>}
              <strong>{form.image ? form.image.name : editing?.image_path ? '画像を変更' : '画像を追加'}</strong>
              <small>JPEG・PNG・WebP、5MBまで</small>
            </label>
          </div>
        </>}
      </div>
    </DialogContent><DialogActions><DsButton type="button" onClick={() => setEditing(undefined)} disabled={saving}>キャンセル</DsButton><DsButton tone="primary" onClick={() => void save()} disabled={saving}>{saving ? '保存中…' : '保存する'}</DsButton></DialogActions></Dialog>
  </div>;
};

export const WithdrawalQuestionnairePage: React.FC = () => {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [itemType, setItemType] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const download = async () => {
    setLoading(true); setError('');
    try {
      const response = await apiClient.get('/api/admin/content/withdrawal-questionnaires/export', {
        params: {
          ...(from ? { created_at_from: from } : {}),
          ...(to ? { created_at_to: to } : {}),
          item_type: itemType,
        },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a'); anchor.href = url; anchor.download = 'withdraw-questions.csv'; anchor.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { setError((err as AxiosError).response?.status === 401 ? 'ログインの有効期限が切れました。' : 'CSVのダウンロードに失敗しました。時間をおいて再度お試しください。'); }
    finally { setLoading(false); }
  };
  return <div className="management-page content-management-page">
    <div className="management-heading"><div><h1>解約アンケート管理</h1><p>回答データをCSVでダウンロード</p></div></div>
    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    <section className="management-panel content-export-panel" aria-label="CSV出力条件"><div className="management-results-head"><h2>出力条件</h2></div><div className="content-export-fields">
      <DsField label="開始日"><input className="ds-input" type="date" value={from} onChange={event => setFrom(event.target.value)} /></DsField>
      <DsField label="終了日"><input className="ds-input" type="date" value={to} onChange={event => setTo(event.target.value)} /></DsField>
      <DsField label="商品種別"><select className="ds-select" value={itemType} onChange={event => setItemType(Number(event.target.value))}><option value={1}>ピル</option><option value={4}>ミニピル</option><option value={2}>スキンケア</option><option value={3}>デリケートソープ</option></select></DsField>
      <DsButton tone="primary" icon={<Download />} disabled={loading} onClick={() => void download()}>{loading ? '出力中…' : 'CSVダウンロード'}</DsButton>
    </div></section>
  </div>;
};
